import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useAuth } from '../../data/hooks/auth'
import { AuthSession, useAuthStore } from '../../data/stores/auth.store'

type TokensMessage = {
  data?: AuthSession
  target: string
  type: 'exchange_token' | 'refresh_token' | 'sign_out' | 'cookies_unsupported'
}
type IncomingTokensMessage = {
  target: string
  action: 'refresh_token'
}

export default function Tokens() {
  const { isAuthenticated, session, loadSession } = useAuthStore()
  const { refreshToken, signOut } = useAuth()
  const refreshTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const hasInitializedRef = useRef(false)
  const isRefreshingRef = useRef(false)

  const parentUrl = useMemo(() => {
    const parentUrl = new URL(document.referrer)
    return parentUrl.href
  }, [])

  const has3dpartyCookieAccess = useMemo(() => {
    const COOKIE_NAME = '__odin_third_party_cookie_test'
    const COOKIE_VALUE = '1'
    console.log('ODIN - INFO -  Checking third-party cookies')

    // Attempt to set third-party cookie
    // eslint-disable-next-line no-useless-concat
    document.cookie = `${COOKIE_NAME}=${COOKIE_VALUE}; ` + `SameSite=None; Secure; path=/`

    // Attempt to read it back
    const cookiesEnabled: boolean = document.cookie.includes(`${COOKIE_NAME}=${COOKIE_VALUE}`)
    console.log(`ODIN - INFO -  Third-party cookies enabled: ${cookiesEnabled}`)
    return cookiesEnabled
  }, [])

  const handleSendMessage = useCallback(
    (type: TokensMessage['type'], includeSession = true) => {
      const message: TokensMessage = {
        target: 'odin-auth-client',
        type,
        data: includeSession ? session : undefined,
      }
      window.parent.postMessage({ ...message }, parentUrl)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(session), parentUrl],
  )

  useEffect(() => {
    loadSession()
  }, [loadSession])

  // Check session
  useEffect(() => {
    if (!has3dpartyCookieAccess) {
      console.log('ODIN - WARN -  Third-party cookies are disabled')
      handleSendMessage('cookies_unsupported', false)
      return
    }

    if (session && isAuthenticated) {
      hasInitializedRef.current = true
      handleSendMessage('exchange_token')
    } else if (!isAuthenticated && hasInitializedRef.current) {
      // Only send sign_out if we were previously authenticated (not on initial load)
      handleSendMessage('sign_out', false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(session), isAuthenticated, handleSendMessage, has3dpartyCookieAccess])

  // Set a timeout to refresh token 1 minute before expiry
  useEffect(() => {
    if (!session?.idTokenDecoded?.exp) return

    // Clear any previous timeout
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current)
    }

    const exp = session.idTokenDecoded.exp * 1000 // exp is in seconds, convert to ms
    const now = Date.now()
    const msUntilRefresh = exp - now - 60_000 // 1 minute before expiry

    const refreshAndExchange = async () => {
      // Prevent concurrent refresh requests
      if (isRefreshingRef.current) return
      isRefreshingRef.current = true

      try {
        await refreshToken()
        // Session update will trigger the effect which sends exchange_token
      } catch (error) {
        signOut() // This will trigger the effect which sends sign_out
      } finally {
        isRefreshingRef.current = false
      }
    }

    if (msUntilRefresh > 0) {
      refreshTimeoutRef.current = setTimeout(refreshAndExchange, msUntilRefresh)
    } else {
      // If already expired or less than 1 min left, refresh and exchange immediately
      refreshAndExchange()
    }

    // Cleanup on unmount
    return () => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.idTokenDecoded?.exp, refreshToken, signOut])

  // Listen from father in case of iframe
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      const data = event.data as IncomingTokensMessage
      // Ensure the message comes from the expected origin
      // if (event.origin !== parentUrl.) {
      //   console.log('ODIN - WARN -  Ignoring message from unknown origin:', event.origin)
      //   return
      // }

      if (data.action === 'refresh_token') {
        console.log('ODIN - INFO -  Received refresh_token request from parent')
        // Prevent concurrent refresh requests
        if (isRefreshingRef.current) return
        isRefreshingRef.current = true

        refreshToken()
          .catch((error) => {
            console.log('ODIN - ERROR -  Error refreshing token:', error)
            signOut() // This will trigger the effect which sends sign_out
          })
          .finally(() => {
            isRefreshingRef.current = false
          })
      }
    }

    // Add the event listener
    window.addEventListener('message', handleMessage)

    // Cleanup
    return () => {
      window.removeEventListener('message', handleMessage)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshToken, signOut])

  return <p>ODIN Token exchange</p>
}
